import { useState } from "react";
import { motion } from "framer-motion";
import { Expand, X } from "lucide-react";
import { GALLERY } from "@/content";

export default function Gallery() {
  const [active, setActive] = useState(null);

  return (
    <section id="our-work" data-testid="photo-gallery-section" className="bg-[#FBF9F1] text-[#232D27] scroll-mt-20">
      <div className="mx-auto max-w-6xl px-5 py-20 lg:py-28">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="overline-gold mb-4">Our Work</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1B3B2B] leading-tight max-w-2xl">
            Gatherings we've <span className="italic text-[#9E7D1E]">lovingly styled.</span>
          </h2>
          <p className="mt-4 text-base text-[#3A4D41] max-w-xl">
            Real tables, real sisterhood, real golden hours — straight from our gatherings.
          </p>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {GALLERY.map((g, i) => (
            <motion.button
              key={g.src}
              data-testid={`gallery-item-${i}`}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 2) * 0.12 }}
              onClick={() => setActive(g)}
              className="group relative rounded-3xl overflow-hidden text-left focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
            >
              <img
                src={g.src}
                alt={g.title}
                loading="lazy"
                className="w-full h-72 sm:h-80 object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#12281D]/85 via-transparent to-transparent" />
              <div className="absolute top-4 right-4 w-9 h-9 rounded-full bg-[#12281D]/60 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <Expand size={15} className="text-[#EED88F]" />
              </div>
              <div className="absolute bottom-0 inset-x-0 p-6">
                <h3 className="font-serif text-2xl text-[#FBF9F1]">{g.title}</h3>
                <p className="text-xs text-[#D1DDD5]/90 mt-1">{g.caption}</p>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {active && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 bg-[#0F2018]/90 backdrop-blur-sm"
            onClick={() => setActive(null)}
          />
          <div className="relative max-w-3xl w-full">
            <button
              data-testid="gallery-lightbox-close"
              aria-label="Close"
              onClick={() => setActive(null)}
              className="absolute -top-12 right-0 text-[#FBF9F1]/80 hover:text-[#D4AF37] transition-colors"
            >
              <X size={26} />
            </button>
            <img
              src={active.src}
              alt={active.title}
              className="w-full max-h-[70vh] object-contain rounded-2xl gold-frame"
            />
            <p className="mt-4 text-center font-serif text-xl text-[#FBF9F1]">{active.title}</p>
            <p className="text-center text-sm text-[#D1DDD5]/80 mt-1">{active.caption}</p>
          </div>
        </div>
      )}
    </section>
  );
}
